import { useEffect, useMemo, useRef, useState } from "react";
import { AppError } from "../../../../stores/models/errors/AppError";
import { ITermsOfServiceFormValues } from "../../../authentication/_interfaces/ITermsOfServiceFormValues";
import { VolunteerRegistrationFlowExternalState, VolunteerSelectionStepValues } from "../_interfaces/IAdminVolunteerRegistrationStepTypeValues";
import { IAdminVolunteerRegistrationFlowValues } from "../_interfaces/IAdminVolunteerRegistrationFlowValues";
import { AdminVolunteerRegistrationValidation } from "../validation/AdminVolunteerRegistrationValidation";
import { useRootStore } from "../../../../shared/general/hooks";
import { TableController } from "../../../../stores/models/TableController";
import { IVolunteerOverview } from "../../../../stores/models/VolunteerOverview";
import useUpdateFilterSelections from "../../../../components/Shared/Hooks/FilterSelections";
import { OptionCollection } from "../../../../stores/models/OptionCollection";
import { StubOrganizationVolunteer } from "../../../../stores/models/StubOrganizationVolunteer";
import { VolunteerDetails } from "../../../../stores/models/VolunteerDetails";
import { VolunteerFilterOptionCollections } from "../../../../components/AccountSettings/Pages/Organization/Volunteers/VolunteersFilterPopper";
import { Shift } from "../../../../stores/models/Shift";
import { AppErrors } from "../../../../stores/models/constants/AppErrors";

enum FilterType {
    Status = 'status',
    Role = 'role'
}

export function useVolunteerSelectionController(
    values: VolunteerSelectionStepValues,
    allValues: IAdminVolunteerRegistrationFlowValues,
    externallyManagedState: VolunteerRegistrationFlowExternalState,
    update: (patch: Partial<VolunteerSelectionStepValues>) => void,
    onSuccess: (override?: Partial<VolunteerSelectionStepValues>) => void
): {
    // Values that affect the overall flow state
    values: VolunteerSelectionStepValues,
    // Values that only need to persist for this particular step:
    stepBoundValues: VolunteerFilterOptionCollections & { isOneOffShift: boolean },
    onChange: (patch: Partial<VolunteerSelectionStepValues>) => void,
    onSubmit: (override?: Partial<VolunteerSelectionStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof VolunteerSelectionStepValues, string>>
} {

    const rootStore = useRootStore();
    const volunteerStore = rootStore.volunteerStore;

    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ITermsOfServiceFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);
    const [isOneOffShift] = useState(allValues.offsets.length === 0);

    // Table filtering setup:

    const organizationVolunteerStatuses = useMemo(() => rootStore.userStore.user.organization?.volunteerStatuses || [], [rootStore.userStore.user.organization?.volunteerStatuses]);
    const organizationVolunteerRoles = useMemo(() => rootStore.userStore.user.organization?.roles || [], [rootStore.userStore.user.organization?.roles]);

    const roleOptions = useMemo(() => new OptionCollection('id', organizationVolunteerRoles), [organizationVolunteerRoles.length]);
    const statusOptions = useMemo(() => new OptionCollection('id', organizationVolunteerStatuses), [organizationVolunteerStatuses.length]);

    useUpdateFilterSelections(values.volunteersTableController, FilterType.Status, statusOptions);
    useUpdateFilterSelections(values.volunteersTableController, FilterType.Role, roleOptions);

    // Set initial flow values:

    useEffect(() => {
        let patch: Partial<VolunteerSelectionStepValues>;
        if (values.backClicked) {
            patch = {
                backClicked: false,
                loading: false,
                succeeded: false,
                viewVolunteers: false,
            }
        } else {
            patch = {
                backClicked: false,
                loading: false,
                volunteerIds: [],
                succeeded: false,
                viewVolunteers: false,
                volunteersTableController: new TableController<'id', IVolunteerOverview, StubOrganizationVolunteer, VolunteerDetails>({
                    records: new OptionCollection('id', [] as IVolunteerOverview[]),
                    persistentSelections: new OptionCollection('id', [] as IVolunteerOverview[]),
                    loadRecords: volunteerStore.getVolunteersForOrganization,
                })
            };
        }
        update(patch);
    }, []);

    // Effects:

    // Close dialog upon successful registration
    useEffect(() => {
        if (values.succeeded) {
            externallyManagedState.dialogState.setOpen(false);
        }
    }, [values.succeeded]);

    useEffect(() => {
        externallyManagedState.dialogState.setLoading(values.loading);
    }, [values.loading]);

    useEffect(() => {
        values.volunteersTableController?.records.options.forEach(option => {
            const alreadyRegistered = externallyManagedState.registeredVolunteerIds.findIndex(id => id === option.object.id) !== -1;
            if (alreadyRegistered) {
                option.setDisabled(true);
            }
        })
    }, [values.volunteersTableController?.records.options.length])

    useEffect(() => {
        if (values.volunteersTableController) {
            update({ volunteerIds: values.volunteersTableController?.persistentSelections?.optionObjects.map(volunteer => volunteer.id) })
        }
    }, [values.volunteersTableController?.persistentSelections?.options.length])

    useEffect(() => {
        if (validationRun && !isSubmitting) {
            const errors = validate().errors;
            setFieldErrors(errors);
        }
    }, [values.volunteerIds, validationRun])


    const validate = (override?: Partial<VolunteerSelectionStepValues>) => {
        return AdminVolunteerRegistrationValidation({ ...externallyManagedState, ...allValues, ...override });
    }

    const isValidSubmission = (override?: Partial<VolunteerSelectionStepValues>) => {
        return validate(override).valid;
    }

    const submit = async (override?: Partial<VolunteerSelectionStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        const effectiveValues = {
            ...allValues,
            ...override
        };

        try {
            const shouldSwitchStepsWithoutValidation = effectiveValues.backClicked;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override, loading: true });
                onSuccess(override);
            } else if (isValidSubmission(effectiveValues)) {
                externallyManagedState.dialogState.setLoading(true);
                if (isOneOffShift) {
                    update({ loading: true });
                    const result = await rootStore.shiftStore.registerVolunteers(
                        effectiveValues.volunteerIds,
                        externallyManagedState.topLevelShift.id,
                        effectiveValues.offsets
                    );
                    update({ ...override, loading: false, succeeded: result !== undefined });
                    onSuccess({ ...override, loading: false, succeeded: result !== undefined });
                    if (allValues.onVolunteersRegistered && result) {
                        allValues.onVolunteersRegistered(result);
                    }
                    externallyManagedState.dialogState.setLoading(false);
                } else {
                    update({ ...override, loading: true });

                    // Wrapped in setTimeout to force render of loading indicator
                    // before performing the next step's calculations for shift 
                    // instances, which can block that render
                    setTimeout(() => {
                        onSuccess(override);
                    }, 0)
                }
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    case AppErrors.LIMIT_EXCEEDED.code:
                        setError("There are not enough remaining slots.")
                        break;
                    default:
                        setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
            externallyManagedState.dialogState.setLoading(false);
            update({ loading: false });
        } finally {
            setSubmitting(false);
        }
    };

    // Set up onClick handler:

    const submitRef = useRef(submit);

    useEffect(() => {
        submitRef.current = submit;
    }, [submit]);

    useEffect(() => {
        const handler = () => {
            submitRef.current();
        };

        externallyManagedState.dialogState.setPrimaryButton({
            children: isOneOffShift ? "Register" : "Next",
            onClick: handler
        })

        externallyManagedState.dialogState.setLeftButton();
        externallyManagedState.dialogState.setHideCancelButton(false);
        externallyManagedState.dialogState.setDisableLoadingSpinnerShrink(true); // Used to avoid issues due to shift instance calculations for next step blocking rendering
    }, []);

    return {
        values,
        stepBoundValues: {
            statusOptions,
            roleOptions,
            isOneOffShift
        },
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}